import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BrandL,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { TaskPickerDialogLayout_task } from './__generated__/index.graphql.ts';
import headerBg from './assets/header-bg.webp';
import vaultHeaderBg from './assets/vault-header-bg.webp';

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100%;
  border-top: 1px solid var(--c-nd-150);
  grid-template-rows: auto 1fr;

  @media ${tabletAndAbove} {
    height: 100%;
    grid-template-rows: 1fr;
    grid-template-columns: 2fr 3fr;
  }
`;

const Column = styled(Vertical)`
  flex: 1;
  padding: var(--double-unit);
  min-width: 0;

  @media ${tabletAndAbove} {
    height: 100%;
    overflow-y: auto;
    padding: var(--triple-unit);
  }

  &:first-child {
    border-right: 1px solid var(--c-nd-150);
    background-image: var(--task-picker-dialog-header-bg);
    background-position: top;
    background-repeat: no-repeat;
    background-size: 100%;
  }
`;

const StyledDialog = styled(Dialog)`
  height: 100%;
  width: 100%;
`;

type Props = {
  task: TaskPickerDialogLayout_task;
  children: ReactNode;
  leftColumnChildren: ReactNode;
  onClose: () => void;
};
export const TaskPickerDialogLayout = ({
  task,
  children,
  leftColumnChildren,
  onClose,
}: Props) => {
  const { title, description } = task;
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const columnHeaderBg =
    isType(task, 'DecisivePlayerPickerTask') && task.sealed
      ? vaultHeaderBg
      : headerBg;

  return (
    <StyledDialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={
        <LabelL className="text-center" bold>
          <FormattedMessage defaultMessage="Daily Mission" />
        </LabelL>
      }
    >
      <Root>
        <Column
          gap={2}
          style={
            {
              '--task-picker-dialog-header-bg': `url(${columnHeaderBg})`,
            } as CSSProperties
          }
        >
          {!!dailyTasksExpiresAt && (
            <Horizontal>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage {...glossary.daily} />
              </LabelM>
              •
              <LabelM color="var(--c-yellow-400)" bold>
                <TimeLeft time={dailyTasksExpiresAt} />
              </LabelM>
            </Horizontal>
          )}
          <BrandL>{title}</BrandL>
          <BodyM>{description}</BodyM>
          {leftColumnChildren}
        </Column>
        <Column>{children}</Column>
      </Root>
    </StyledDialog>
  );
};

TaskPickerDialogLayout.fragments = {
  task: gql`
    fragment TaskPickerDialogLayout_task on TaskInterface {
      id
      title
      description
      ... on DecisivePlayerPickerTask {
        id
        sealed
      }
    }
  ` as TypedDocumentNode<TaskPickerDialogLayout_task>,
};
